import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { getWhatsAppLink } from "@/lib/whatsapp";

const SaleSection = () => {
  const brands = [
    {
      name: "Mitsubishi Heavy Duty",
      logo: "/images/brands/mitsubishi-heavy.jpg",
      types: ["VRF", "Duct", "Cassette", "Split"]
    },
    {
      name: "Mitsubishi Electric",
      logo: "/images/brands/mitsubishi-electric.jpg",
      types: ["VRF", "Duct", "Cassette", "Split"]
    },
    {
      name: "O General",
      logo: "/images/brands/o-general.jpg",
      types: ["VRF", "Duct", "Cassette", "Split"]
    },
    {
      name: "Daikin",
      logo: "/images/brands/daikin.png",
      types: ["VRF", "Duct", "Cassette", "Split"]
    },
    {
      name: "Blue Star",
      logo: "/images/brands/blue-star.png",
      types: ["VRF", "Duct", "Cassette", "Split"]
    },
    {
      name: "Toshiba",
      logo: "/images/brands/toshiba.png",
      types: ["VRF", "Duct", "Cassette", "Split"]
    },
    {
      name: "Panasonic",
      logo: "/images/brands/panasonic.jpg",
      types: ["VRF", "Duct", "Cassette", "Split"]
    },
    {
      name: "Cruise",
      logo: "/images/brands/cruise.jpg",
      types: ["Duct", "Cassette", "Split"]
    },
    {
      name: "TCL",
      logo: "/images/brands/tcl.svg",
      types: ["Split"]
    },
    {
      name: "Wybor",
      logo: "/images/brands/wybor.png",
      types: ["Split"]
    },
    {
      name: "Amstrad",
      logo: "/images/brands/amstrad.png",
      types: ["Split"]
    },
    {
      name: "Hitachi",
      logo: "/images/brands/hitachi.png",
      types: ["VRF", "Duct", "Cassette", "Split"]
    }
  ];

  const [selectedBrand, setSelectedBrand] = useState(null);

  return (
    <section id="sale" className="py-20 bg-gradient-to-br from-black via-gray-900 to-black" data-testid="sale-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 mb-4">AC Sales</Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-testid="sale-title">
            Premium HVAC Brands We Deal In
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Authorized dealer for top HVAC brands. Get the best prices on VRF, Ductable, Cassette & Split AC systems.
          </p>
        </div>

        {/* Brands Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {brands.map((brand, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer"
              onClick={() => setSelectedBrand(selectedBrand === index ? null : index)}
              data-testid={`brand-card-${index}`}
            >
              {/* Brand Logo */}
              <div className="h-32 flex items-center justify-center mb-4 p-2 bg-white rounded-xl overflow-hidden">
                <img
                  src={brand.logo}
                  alt={brand.name}
                  className="w-full h-full object-contain"
                />
              </div>
              
              {/* Brand Name */}
              <h3 className="text-center font-semibold text-gray-900 mb-3 text-sm">{brand.name}</h3>
              
              {/* AC Types */}
              <div className="flex flex-wrap justify-center gap-2 mb-4">
                {brand.types.map((type, idx) => (
                  <span
                    key={idx}
                    className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium"
                  >
                    {type}
                  </span>
                ))}
              </div>

              {/* Expanded Options */}
              {selectedBrand === index && (
                <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                  {brand.types.map((type, idx) => (
                    <a
                      key={idx}
                      href={getWhatsAppLink(`Hi! I'm interested in buying ${brand.name} ${type} AC. Please share the details and price.`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-2 bg-gray-50 hover:bg-amber-50 rounded-lg transition-colors group"
                    >
                      <span className="text-sm font-medium text-gray-700">{type} AC</span>
                      <span className="text-xs text-amber-600 group-hover:text-amber-700">Get Quote →</span>
                    </a>
                  ))}
                </div>
              )}

              {/* WhatsApp Button */}
              <a
                href={getWhatsAppLink(`Hi! I'm interested in ${brand.name} AC products. Please share the available models and prices.`)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 flex items-center justify-center gap-2 w-full bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-4 rounded-lg transition-all text-sm"
                onClick={(e) => e.stopPropagation()}
                data-testid={`brand-whatsapp-${index}`}
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Enquire on WhatsApp
              </a>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-amber-500/20 to-yellow-500/20 rounded-2xl p-8 border border-amber-500/30">
            <h3 className="text-2xl font-bold text-white mb-4">Looking for Best Deals on AC?</h3>
            <p className="text-gray-400 mb-6">Get exclusive prices on all brands. Contact us for bulk orders and project requirements.</p>
            <a
              href={getWhatsAppLink("Hi! I'm looking for the best deals on AC. Please share your current offers and prices.")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-semibold px-8 py-3 rounded-lg transition-all"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Get Best Deals Now
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SaleSection;
